import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { db } from "../../firebaseconf";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import Preloader from "../../components/preloader";
import { COLLEGE_SEMESTERS } from "../../utils/semesterDefaults";

import Header from "../../components/header";
import {
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Grid,
} from "@mui/material";
import PostAddIcon from '@mui/icons-material/PostAdd';
import CloseIcon from '@mui/icons-material/Close';
import Alerts from "../../components/alert";

export default function Assignment() {
    const today = new Date();
    const fulldate = today.toISOString().split('T')[0];
    const [loading, setLoading] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [error, setError] = useState('');
    const [semesters, setSemesters] = useState([]);
    const [selectedSemester, setSelectedSemester] = useState("");
    const [selectedSubject, setselectedSubject] = useState([]);
    const [students, setStudents] = useState([]);
    const [formVisible, setFormVisible] = useState(false);

    const [newAssignment, setNewAssignment] = useState({
        title: "",
        description: "",
        subject: "",
        semester: "",
        deadline: "",
        Grades: "",
    });

    // Fixed: Fetch specific student data and all students
    useEffect(() => {
        const fetchDataAsync = async () => {
            try {
                const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
                if (!loggedUser || !loggedUser.institution_id) return;

                const institutionId = loggedUser.institution_id;

                // Fetch all students
                const studentsRef = collection(db, 'institutions', institutionId, 'students');
                const allStudentsSnapshot = await getDocs(studentsRef);
                const allStudents = allStudentsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setStudents(allStudents);
            } catch (error) {
                console.error("Error fetching students:", error);
            }
        };

        fetchDataAsync();
    }, []);

    useEffect(() => {
        const fetchSemesters = async () => {
            try {
                const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
                if (!loggedUser || !loggedUser.institution_id) return;

                const institutionId = loggedUser.institution_id;
                
                // Fetch semesters from institution metadata or a semesters collection
                const semestersRef = collection(db, 'institutions', institutionId, 'semesters');
                const semestersSnapshot = await getDocs(semestersRef);
                
                if (!semestersSnapshot.empty) {
                    const semestersList = semestersSnapshot.docs.map(doc => doc.id || doc.data().name);
                    setSemesters(semestersList);
                } else {
                    // Try fetching from subjects collection
                    const subjectsRef = collection(db, 'institutions', institutionId, 'subjects');
                    const subjectsSnapshot = await getDocs(subjectsRef);
                    
                    if (!subjectsSnapshot.empty) {
                        const uniqueSemesters = [...new Set(subjectsSnapshot.docs.map(doc => doc.data().semester))];
                        setSemesters(uniqueSemesters.length > 0 ? uniqueSemesters : COLLEGE_SEMESTERS);
                    } else {
                        // Use fallback semesters
                        setSemesters(COLLEGE_SEMESTERS);
                    }
                }
            } catch (error) {
                console.error("Error fetching semesters:", error);
                // Fallback
                setSemesters(['semester1', 'semester2', 'semester3', 'semester4']);
            }
        };

        fetchSemesters();
    }, []);

    useEffect(() => {
        const fetchSubjects = async () => {
            if (selectedSemester) {
                try {
                    const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
                    if (!loggedUser || !loggedUser.institution_id) return;

                    const institutionId = loggedUser.institution_id;
                    
                    // Fetch subjects for the selected semester
                    const subjectsRef = collection(db, 'institutions', institutionId, 'subjects');
                    const subjectsQuery = query(subjectsRef, where('semester', '==', selectedSemester));
                    const subjectsSnapshot = await getDocs(subjectsQuery);

                    const subjectsList = subjectsSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setselectedSubject(subjectsList);
                } catch (error) {
                    console.error("Error fetching subjects:", error);
                    setselectedSubject([]);
                }
            } else {
                setselectedSubject([]);
            }
        };

        fetchSubjects();
    }, [selectedSemester]);

    const handleAssignmentChange = (key, value) => {
        setNewAssignment((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (new Date(newAssignment.deadline) < new Date(fulldate)) {
            setError("Error: Deadline cannot be earlier than the creation date.");
            return;
        }

        setLoading(true);
        setError('');

        try {
            const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
            if (!loggedUser || !loggedUser.institution_id) {
                throw new Error("Institution ID not found");
            }

            const institutionId = loggedUser.institution_id;

            // Update assignment for each student
            for (const student of students) {
                const studentRef = doc(db, 'institutions', institutionId, 'students', student.id);

                await updateDoc(studentRef, {
                    [`Assignment.${selectedSemester}.${newAssignment.subject}`]: {
                        ...newAssignment,
                        createdDate: fulldate,
                    }
                });
            }

            setAlertVisible(true);
            setTimeout(() => setAlertVisible(false), 3000);
            setTimeout(() => window.location.reload(), 3500);
        } catch (error) {
            console.error("Error submitting assignment:", error);
            setError(`❌ ${error.message || 'Failed to add assignment. Please try again.'}`);
        } finally {
            setLoading(false);
            // Reset form
            setNewAssignment({
                title: "",
                description: "",
                subject: "",
                semester: "",
                deadline: "",
                Grades: "",
            });
        }
    };

    return (
        <Layout>
            <Header title="Add Assignment" />
            <Box sx={{ padding: "2rem" }}>
                {alertVisible && <Alerts message="Assignment Added successfully!" type="success" color="success" />}
                {error && <Alerts message={error} type="error" color="error" />}
                {loading && <Preloader />}

                <Button
                    variant="contained"
                    size="large"
                    onClick={() => setFormVisible((prev) => !prev)}
                    sx={{ marginBottom: "1rem" }}
                >
                    {formVisible ? <CloseIcon /> : <PostAddIcon />}
                    {formVisible ? "Hide Form" : "Add Assignment"}
                </Button>

                {formVisible && (
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Assignment Title"
                                    value={newAssignment.title}
                                    onChange={(e) => handleAssignmentChange("title", e.target.value)}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    type="date"
                                    label="Deadline"
                                    InputLabelProps={{ shrink: true }}
                                    value={newAssignment.deadline}
                                    onChange={(e) => handleAssignmentChange("deadline", e.target.value)}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    type="text"
                                    label="Grade"
                                    value={newAssignment.Grades}
                                    onChange={(e) => handleAssignmentChange("Grades", e.target.value)}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Select Semester</InputLabel>
                                    <Select
                                        value={selectedSemester}
                                        onChange={(e) => { 
                                            handleAssignmentChange("semester", e.target.value); 
                                            setSelectedSemester(e.target.value); 
                                        }}
                                        required 
                                    >
                                        <MenuItem value="">Select Semester</MenuItem>
                                        {semesters.map((semester) => (
                                            <MenuItem key={semester} value={semester}>{semester}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Select Subject</InputLabel>
                                    <Select
                                        value={newAssignment.subject}
                                        onChange={(e) => { 
                                            handleAssignmentChange("subject", e.target.value); 

                                        }}
                                        required
                                        disabled={!selectedSubject.length}
                                    >
                                        <MenuItem value="">Select Subject</MenuItem>
                                        {selectedSubject.map((subject, index) => (
                                            <MenuItem key={subject.id || index} value={subject.subject || subject.name}>{subject.subject || subject.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Description"
                                    multiline
                                    rows={4}
                                    value={newAssignment.description}
                                    onChange={(e) => handleAssignmentChange("description", e.target.value)}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                >
                                    Submit
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Box>
        </Layout>
    );
}